import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import {
  BeforeAfterGalleryPage,
  ContactPage,
  FaqPage,
  LegalPage,
  AboutPage,
  ProjectDetailPage,
  ServicesIndexPage,
  ThankYouPage,
} from "@/components/pages/CorePages";
import { BlogIndexPage, BlogPostPage, getBlogPost } from "@/components/pages/BlogPages";
import { ServiceAreaPage } from "@/components/pages/ServiceAreaPage";
import { ServiceAreasIndexPage } from "@/components/pages/ServiceAreasIndexPage";
import { ServiceDetailPage } from "@/components/pages/ServiceDetailPage";
import { PpcLandingPage } from "@/components/pages/PpcLandingPage";
import { ExpansionPage } from "@/components/pages/ExpansionPages";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { business } from "@/config/business";
import { findPpcCampaign } from "@/config/campaigns";
import { getProjectBySlug } from "@/config/projects";
import { findServiceArea } from "@/config/service-areas";
import { serviceBySlug } from "@/config/services";
import { I18nProvider, localizePath, parseLocalizedPath, resolveLegacyRedirect } from "@/i18n";
import { getPageUi, localizeServiceArea, localizeServicePage, type PageUi } from "@/i18n/content";
import { HomePage } from "./-home-page";
import { metadataToRouteHead, resolvePageMetadata } from "@/lib/page-meta";
import {
  buildArticleJsonLd,
  buildFaqPageJsonLd,
  buildRoofingContractorJsonLd,
  buildServiceJsonLd,
  buildWebPageJsonLd,
  buildWebSiteJsonLd,
} from "@/lib/business-json-ld";
import { englishFaqSchemaItems } from "@/config/site-faq";
import { LOCALES } from "@/i18n/locale-config";
import { accessibilityCopy } from "@/i18n/accessibility";
import { getNotFoundCopy, notFoundDocumentTitle } from "@/i18n/not-found";
import { getMarketingCopy } from "@/i18n/marketing-copy";
import { localizeProject } from "@/i18n/project-content";
import {
  encodeRedirectPath,
  localizedPath,
  resolveRouteFromPath,
  type RouteId,
} from "@/i18n/route-manifest";
import type { LocaleCode } from "@/i18n/locale-config";

type ExpansionPageKind =
  | "emergency-roofing"
  | "commercial-roofing"
  | "strata-roofing"
  | "residential-roofing"
  | "roof-systems"
  | "roof-estimate"
  | "british-columbia";

type ExpansionRouteId =
  | "core:emergency-roofing"
  | "core:commercial-roofing"
  | "core:strata-roofing"
  | "core:residential-roofing"
  | "core:roof-systems"
  | "core:roof-estimate"
  | "core:british-columbia";

const EXPANSION_PAGE_KIND_BY_ROUTE_ID: Readonly<Record<ExpansionRouteId, ExpansionPageKind>> = {
  "core:emergency-roofing": "emergency-roofing",
  "core:commercial-roofing": "commercial-roofing",
  "core:strata-roofing": "strata-roofing",
  "core:residential-roofing": "residential-roofing",
  "core:roof-systems": "roof-systems",
  "core:roof-estimate": "roof-estimate",
  "core:british-columbia": "british-columbia",
};

const EXPANSION_SERVICE_AREA_BY_ROUTE_ID: Readonly<
  Partial<Record<ExpansionRouteId, readonly string[]>>
> = {
  "core:emergency-roofing": ["Lower Mainland, British Columbia"],
  "core:commercial-roofing": ["British Columbia"],
  "core:strata-roofing": ["British Columbia"],
  "core:residential-roofing": ["British Columbia"],
  "core:roof-systems": ["British Columbia"],
};

function expansionPageKind(routeId: RouteId | null): ExpansionPageKind | null {
  if (!routeId || !(routeId in EXPANSION_PAGE_KIND_BY_ROUTE_ID)) return null;
  return EXPANSION_PAGE_KIND_BY_ROUTE_ID[routeId as ExpansionRouteId];
}

export const Route = createFileRoute("/$")({
  beforeLoad: ({ params, location }) => {
    const publicPath = `/${params._splat ?? ""}`;
    const redirectTarget = resolveLegacyRedirect(publicPath);
    if (redirectTarget) {
      const hash = location.hash ? `#${location.hash.replace(/^#/, "")}` : "";
      throw redirect({
        href: `${encodeRedirectPath(redirectTarget.target)}${location.searchStr ?? ""}${hash}`,
        statusCode: 301,
        replace: true,
      });
    }

    const resolved = resolveRouteFromPath(publicPath);
    if (resolved?.route) return;

    if (resolvePageMetadata(publicPath)) return;

    throw notFound();
  },
  head: ({ params }) => {
    const publicPath = `/${params._splat ?? ""}`;
    const metadata = resolvePageMetadata(publicPath);
    if (metadata) {
      const head = metadataToRouteHead(metadata);
      const ui = getPageUi(metadata.locale);
      const localizedPageName = metadata.title.split(/[|｜]/)[0]?.trim() ?? metadata.title;
      const breadcrumbItems = buildBreadcrumbs(
        metadata.routeId,
        metadata.locale,
        ui,
        localizedPageName,
      );
      const scripts = [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbItems.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.name,
              item: item.item,
            })),
          }),
        },
      ];
      const language = LOCALES.find((locale) => locale.routeLocaleKey === metadata.locale)!.bcp47;

      if (metadata.robots === "index, follow") {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify(
            buildWebPageJsonLd({
              url: metadata.canonicalUrl,
              name: metadata.title,
              description: metadata.description,
              inLanguage: language,
              image: metadata.openGraph.image,
            }),
          ),
        });
      }

      const serviceArea = EXPANSION_SERVICE_AREA_BY_ROUTE_ID[metadata.routeId as ExpansionRouteId];
      if (serviceArea) {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify(
            buildServiceJsonLd({
              url: metadata.canonicalUrl,
              name: localizedPageName,
              description: metadata.description,
              serviceType: localizedPageName,
              inLanguage: language,
              areaServed: serviceArea,
            }),
          ),
        });
      }

      if (metadata.routeId === "home" || metadata.routeId === "core:contact") {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify(
            buildRoofingContractorJsonLd({ description: metadata.description }),
          ),
        });
      }

      if (
        metadata.locale === "en-ca" &&
        (metadata.routeId === "home" || metadata.routeId === "core:faq")
      ) {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify(buildFaqPageJsonLd(englishFaqSchemaItems)),
        });
      }

      if (metadata.routeId.startsWith("blog:")) {
        const slug = metadata.routeId.slice("blog:".length);
        const post = getBlogPost(slug);
        if (post) {
          scripts.push({
            type: "application/ld+json",
            children: JSON.stringify(
              buildArticleJsonLd({
                url: metadata.canonicalUrl,
                headline: post.title,
                description: post.metaDescription,
                datePublished: post.publishedAt,
                dateModified: post.updatedAt,
                image: metadata.openGraph.image,
              }),
            ),
          });
          if (post.faqs.length) {
            scripts.push({
              type: "application/ld+json",
              children: JSON.stringify(
                buildFaqPageJsonLd(
                  post.faqs.map((faq) => ({ question: faq.question, answer: faq.answer })),
                ),
              ),
            });
          }
        }
      }

      // WebSite entity is declared once, on the home page of every locale.
      if (metadata.routeId === "home") {
        scripts.push({
          type: "application/ld+json",
          children: JSON.stringify(buildWebSiteJsonLd(LOCALES.map((locale) => locale.bcp47))),
        });
      }

      return {
        ...head,
        scripts,
      };
    }

    const parsed = parseLocalizedPath(publicPath);

    if (parsed.routeId?.startsWith("service:")) {
      const slug = parsed.canonicalPath.slice("/services/".length);
      const service = serviceBySlug[slug];
      if (service) {
        return {
          meta: [
            { title: service.metaTitle },
            { name: "description", content: service.metaDescription },
            { name: "robots", content: "index, follow" },
            { property: "og:title", content: service.metaTitle },
            { property: "og:description", content: service.metaDescription },
          ],
          links: [
            {
              rel: "canonical",
              href: `${business.url}${localizePath(service.path, parsed.locale)}`,
            },
          ],
        };
      }
    }

    const { locale } = parseLocalizedPath(publicPath);
    const copy = getNotFoundCopy(locale);
    return {
      meta: [
        { title: notFoundDocumentTitle(locale) },
        { name: "description", content: copy.body },
        { name: "robots", content: "noindex, nofollow" },
      ],
    };
  },
  component: CatchAllRoute,
});

function CatchAllRoute() {
  const { _splat } = Route.useParams();
  const publicPath = `/${_splat ?? ""}`;
  const parsed = parseLocalizedPath(publicPath);
  const { locale, canonicalPath, routeId } = parsed;
  const path = (href: string) => localizePath(href, locale);

  if (routeId === "home" || canonicalPath === "/") {
    return (
      <I18nProvider initialLocale={locale}>
        <HomePage />
      </I18nProvider>
    );
  }

  const page = resolvePage(routeId, canonicalPath, path, locale, getPageUi(locale));

  if (page.type === "ppc") {
    return (
      <I18nProvider initialLocale={locale}>
        <PpcLandingPage
          campaign={page.campaign}
          buildPath={path}
          phoneHref={business.phoneHref}
          phoneLabel={business.phone}
          privacyPath="/privacy"
        />
      </I18nProvider>
    );
  }

  return <SiteShell locale={locale}>{page.content}</SiteShell>;
}

function resolvePage(
  routeId: RouteId | null,
  canonicalPath: string,
  path: (href: string) => string,
  locale: LocaleCode,
  ui: PageUi | undefined,
) {
  const expansionKind = expansionPageKind(routeId);
  if (expansionKind) {
    return {
      type: "site" as const,
      content: <ExpansionPage kind={expansionKind} path={path} locale={locale} ui={ui} />,
    };
  }

  switch (routeId) {
    case "core:services":
      return {
        type: "site" as const,
        content: <ServicesIndexPage path={path} ui={ui} locale={locale} />,
      };
    case "core:about":
      return { type: "site" as const, content: <AboutPage path={path} ui={ui} /> };
    case "core:before-after":
      return { type: "site" as const, content: <BeforeAfterGalleryPage path={path} ui={ui} /> };
    case "core:blog":
      return { type: "site" as const, content: <BlogIndexPage path={path} ui={ui} /> };
    case "core:service-areas":
      return {
        type: "site" as const,
        content: (
          <ServiceAreasIndexPage buildPath={path} contactPath="/contact" locale={locale} ui={ui} />
        ),
      };
    case "core:faq":
      return { type: "site" as const, content: <FaqPage path={path} ui={ui} /> };
    case "core:contact":
      return { type: "site" as const, content: <ContactPage path={path} ui={ui} /> };
    case "core:privacy":
      return { type: "site" as const, content: <LegalPage kind="privacy" path={path} ui={ui} /> };
    case "core:terms":
      return { type: "site" as const, content: <LegalPage kind="terms" path={path} ui={ui} /> };
    case "core:thank-you":
      return { type: "site" as const, content: <ThankYouPage path={path} ui={ui} /> };
  }

  if (routeId?.startsWith("service:")) {
    const slug = routeId.slice("service:".length);
    const service = serviceBySlug[slug];
    if (service) {
      return {
        type: "site" as const,
        content: (
          <ServiceDetailPage service={localizeServicePage(service, locale)} path={path} ui={ui} />
        ),
      };
    }
  }

  if (routeId?.startsWith("blog:")) {
    const slug = routeId.slice("blog:".length);
    const post = getBlogPost(slug);
    if (post) {
      return {
        type: "site" as const,
        content: <BlogPostPage post={post} path={path} ui={ui} />,
      };
    }
  }

  if (routeId?.startsWith("project:")) {
    const slug = routeId.slice("project:".length);
    const project = getProjectBySlug(slug);
    if (project) {
      return {
        type: "site" as const,
        content: (
          <ProjectDetailPage project={localizeProject(project, locale)} path={path} ui={ui} />
        ),
      };
    }
  }

  if (routeId?.startsWith("area:")) {
    const slug = routeId.slice("area:".length);
    const area = findServiceArea(slug);
    if (area?.confirmed) {
      return {
        type: "site" as const,
        content: (
          <ServiceAreaPage
            area={localizeServiceArea(area, locale)}
            buildPath={path}
            contactPath="/contact"
            ui={ui}
          />
        ),
      };
    }
  }

  if (routeId?.startsWith("campaign:")) {
    const campaign = findPpcCampaign(routeId.slice("campaign:".length));
    if (campaign?.indexable) return { type: "ppc" as const, campaign };
  }

  const notFound = getMarketingCopy(locale)?.notFound;
  const notFoundUi = getPageUi(locale);
  return {
    type: "site" as const,
    content: (
      <section className="grid min-h-[75vh] place-items-center bg-background px-5 pt-28">
        <div className="max-w-lg text-center">
          <p className="eyebrow">
            404 · {notFound?.eyebrow ?? notFoundUi?.corePages?.legal?.legal ?? "Page not found"}
          </p>
          <h1 className="display-1 mt-4">{notFound?.title ?? "This roofline ends here."}</h1>
          <p className="mt-5 leading-7 text-muted-foreground">
            {notFound?.body ??
              "The page may have moved or the address may be incomplete. Return home or browse the roofing services currently available."}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button variant="brand" size="xl" asChild>
              <a href={path("/")}>{notFound?.home ?? "Go home"}</a>
            </Button>
            <Button variant="quiet" size="xl" asChild>
              <a href={path("/services")}>{notFound?.services ?? "View services"}</a>
            </Button>
          </div>
        </div>
      </section>
    ),
  };
}

function buildBreadcrumbs(
  routeId: RouteId,
  locale: LocaleCode,
  ui: PageUi | undefined,
  localizedPageName: string,
) {
  const items: { name: string; item: string }[] = [
    {
      name: accessibilityCopy[locale].home,
      item: `${business.url}${localizedPath("home", locale)}`,
    },
  ];

  if (routeId === "home") return items;

  if (routeId === "core:british-columbia") {
    items.push({
      name: ui?.common.serviceAreas ?? "Service Areas",
      item: `${business.url}${localizedPath("core:service-areas", locale)}`,
    });
    items.push({
      name: localizedPageName,
      item: `${business.url}${localizedPath(routeId, locale)}`,
    });
    return items;
  }

  if (routeId.startsWith("service:")) {
    items.push({
      name: ui?.common.services ?? "Services",
      item: `${business.url}${localizedPath("core:services", locale)}`,
    });
    items.push({
      name: localizedPageName,
      item: `${business.url}${localizedPath(routeId, locale)}`,
    });
    return items;
  }

  if (routeId.startsWith("area:")) {
    items.push({
      name: ui?.common.serviceAreas ?? "Service Areas",
      item: `${business.url}${localizedPath("core:service-areas", locale)}`,
    });
    items.push({
      name: localizedPageName,
      item: `${business.url}${localizedPath(routeId, locale)}`,
    });
    return items;
  }

  items.push({
    name: localizedPageName,
    item: `${business.url}${localizedPath(routeId, locale)}`,
  });
  return items;
}
